import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const WorkOrderForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const isEdit = !!id;
  const token = localStorage.getItem('token');

  const [formData, setFormData] = useState({
    title: '', title_ar: '', description: '', description_ar: '',
    wo_type: 'corrective', priority: 'medium', source: 'manual',
    client_id: '', equipment_id: '', location_id: '', assigned_to: '',
    scheduled_date: '', scheduled_time_start: '', scheduled_time_end: '',
    notes: '', checklist: [],
  });
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDrop, setShowClientDrop] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [showAgentDrop, setShowAgentDrop] = useState(false);

  const [clients, setClients] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [agents, setAgents] = useState([]);
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pre-fill from URL query params and navigation state (e.g. from preventive schedule)
  useEffect(() => {
    if (isEdit) return;
    const qp = Object.fromEntries(searchParams.entries());
    if (Object.keys(qp).length > 0) {
      const updates = {};
      if (qp.client_id) updates.client_id = qp.client_id;
      if (qp.equipment_id) updates.equipment_id = qp.equipment_id;
      if (qp.contract_id) updates.contract_id = qp.contract_id;
      if (qp.wo_type) updates.wo_type = qp.wo_type;
      if (qp.priority) updates.priority = qp.priority;
      if (qp.source) updates.source = qp.source;
      if (qp.title) updates.title = qp.title;
      if (qp.title_ar) updates.title_ar = qp.title_ar;
      if (qp.description) updates.description = qp.description;
      if (qp.scheduled_date) updates.scheduled_date = qp.scheduled_date;
      // Checklist from navigation state
      const navState = location.state || {};
      if (navState.checklist && Array.isArray(navState.checklist) && navState.checklist.length > 0) {
        updates.checklist = navState.checklist;
      }
      if (navState.instructions && !qp.description) {
        updates.description = navState.instructions;
      }
      setFormData(prev => ({ ...prev, ...updates }));
      if (qp.client_id) fetchEquipment(qp.client_id);
    }
  }, []);
  const [saving, setSaving] = useState(false);
  const [checklistInput, setChecklistInput] = useState('');
  const [checklistInputAr, setChecklistInputAr] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Labels
  const L = {
    create_title: isAr ? 'إنشاء أمر عمل' : 'Create Work Order',
    edit_title: isAr ? 'تعديل أمر عمل' : 'Edit Work Order',
    basic_info: isAr ? 'المعلومات الأساسية' : 'Basic Information',
    title: isAr ? 'العنوان' : 'Title',
    title_ar: isAr ? 'العنوان (عربي)' : 'Title (Arabic)',
    description: isAr ? 'الوصف' : 'Description',
    description_ar: isAr ? 'الوصف (عربي)' : 'Description (Arabic)',
    type: isAr ? 'النوع' : 'Type',
    priority: isAr ? 'الأولوية' : 'Priority',
    source: isAr ? 'المصدر' : 'Source',
    equipment_section: isAr ? 'المعدات والعميل' : 'Equipment & Client',
    client: isAr ? 'العميل' : 'Client',
    equipment: isAr ? 'المعدات' : 'Equipment',
    select: isAr ? 'اختر...' : 'Select...',
    entitlement: isAr ? 'التغطية التعاقدية' : 'Contract Entitlement',
    covered: isAr ? 'مغطى' : 'Covered',
    not_covered: isAr ? 'غير مغطى - قابل للفوترة' : 'Not Covered - Billable',
    coverage: isAr ? 'نوع التغطية' : 'Coverage',
    visits_left: isAr ? 'الزيارات المتبقية' : 'Visits Remaining',
    sla_response: isAr ? 'استجابة SLA' : 'SLA Response',
    sla_resolution: isAr ? 'حل SLA' : 'SLA Resolution',
    unlimited: isAr ? 'غير محدود' : 'Unlimited',
    hours: isAr ? 'ساعة' : 'hours',
    contract: isAr ? 'العقد' : 'Contract',
    schedule_section: isAr ? 'الجدولة والتعيين' : 'Schedule & Assignment',
    assigned_to: isAr ? 'مسند إلى' : 'Assigned To',
    scheduled_date: isAr ? 'تاريخ الموعد' : 'Scheduled Date',
    time_start: isAr ? 'وقت البداية' : 'Start Time',
    time_end: isAr ? 'وقت النهاية' : 'End Time',
    checklist_section: isAr ? 'قائمة الفحص' : 'Checklist',
    add_item: isAr ? 'إضافة عنصر' : 'Add Item',
    item_en: isAr ? 'العنصر (إنجليزي)' : 'Item (English)',
    item_ar: isAr ? 'العنصر (عربي)' : 'Item (Arabic)',
    notes_section: isAr ? 'ملاحظات' : 'Notes',
    notes: isAr ? 'ملاحظات داخلية' : 'Internal Notes',
    save: isAr ? 'حفظ' : 'Save',
    saving: isAr ? 'جاري الحفظ...' : 'Saving...',
    cancel: isAr ? 'إلغاء' : 'Cancel',
    remove: isAr ? 'حذف' : 'Remove',
    no_equipment: isAr ? 'اختر عميل أولاً' : 'Select a client first',
    billable: isAr ? 'قابل للفوترة' : 'Billable',
  };

  const TYPE_OPTIONS = [
    { value: 'corrective', label: isAr ? 'تصحيحي' : 'Corrective' },
    { value: 'preventive', label: isAr ? 'وقائي' : 'Preventive' },
    { value: 'inspection', label: isAr ? 'فحص' : 'Inspection' },
    { value: 'emergency', label: isAr ? 'طوارئ' : 'Emergency' },
    { value: 'installation', label: isAr ? 'تركيب' : 'Installation' },
  ];
  const PRIORITY_OPTIONS = [
    { value: 'critical', label: isAr ? 'حرج' : 'Critical' },
    { value: 'high', label: isAr ? 'عالي' : 'High' },
    { value: 'medium', label: isAr ? 'متوسط' : 'Medium' },
    { value: 'low', label: isAr ? 'منخفض' : 'Low' },
  ];
  const SOURCE_OPTIONS = [
    { value: 'manual', label: isAr ? 'يدوي' : 'Manual' },
    { value: 'maintenance_plan', label: isAr ? 'خطة صيانة' : 'Maintenance Plan' },
    { value: 'helpdesk', label: isAr ? 'الدعم' : 'Helpdesk' },
    { value: 'whatsapp_bot', label: isAr ? 'بوت واتساب' : 'WhatsApp Bot' },
    { value: 'qr_scan', label: isAr ? 'مسح QR' : 'QR Scan' },
  ];

  // Styles (Form Style Guide)
  const inputSt = {
    width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  };
  const labelSt = { display: 'block', marginBottom: '8px', color: '#d1d5db', fontSize: '13px', fontWeight: '500' };
  const sectionSt = {
    backgroundColor: 'rgba(26, 32, 56, 0.8)', padding: '25px', borderRadius: '12px',
    marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)',
  };
  const sectionTitleSt = { color: '#f9fafb', fontSize: '16px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' };
  const gridSt = (cols = 2) => ({ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '16px' });
  const btnPrimary = {
    padding: '12px 28px', backgroundColor: '#667eea', color: '#fff', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  };
  const btnOutline = {
    padding: '12px 28px', backgroundColor: 'transparent', color: '#9ca3af',
    border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
  };

  // Fetch clients
  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/fsm/clients?limit=500', { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setClients(data.data || []);
    } catch (err) { console.error('Fetch clients error:', err); }
  }, [token]);

  // Fetch equipment filtered by client
  const fetchEquipment = useCallback(async (clientId) => {
    if (!clientId) { setEquipmentList([]); return; }
    try {
      const res = await fetch(`/api/v1/fsm/equipment?client_id=${clientId}&limit=500`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setEquipmentList(data.data || []);
    } catch (err) { console.error('Fetch equipment error:', err); }
  }, [token]);

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/agents', { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setAgents(data.data || data.agents || []);
    } catch (err) { console.error('Fetch agents error:', err); }
  }, [token]);

  // Fetch entitlement when equipment changes
  const fetchEntitlement = useCallback(async (equipmentId) => {
    if (!equipmentId) { setEntitlement(null); return; }
    try {
      const res = await fetch(`/api/v1/fsm/equipment/${equipmentId}/entitlement-check`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setEntitlement(data.data);
    } catch (err) { console.error('Fetch entitlement error:', err); }
  }, [token]);

  // Fetch existing WO for edit
  const fetchWorkOrder = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/v1/fsm/work-orders/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        const wo = data.data;
        setFormData({
          title: wo.title || '', title_ar: wo.title_ar || '',
          description: wo.description || '', description_ar: wo.description_ar || '',
          wo_type: wo.wo_type || 'corrective', priority: wo.priority || 'medium',
          source: wo.source || 'manual',
          client_id: wo.client_id || '', equipment_id: wo.equipment_id || '',
          location_id: wo.location_id || '', assigned_to: wo.assigned_to || '',
          scheduled_date: wo.scheduled_date ? wo.scheduled_date.split('T')[0] : '',
          scheduled_time_start: wo.scheduled_time_start || '',
          scheduled_time_end: wo.scheduled_time_end || '',
          notes: wo.notes || '',
          checklist: wo.checklist ? (typeof wo.checklist === 'string' ? JSON.parse(wo.checklist) : wo.checklist) : [],
        });
        if (wo.client_id) fetchEquipment(wo.client_id);
        if (wo.equipment_id) fetchEntitlement(wo.equipment_id);
      }
    } catch (err) { console.error('Fetch WO error:', err); }
    finally { setLoading(false); }
  }, [id, token, fetchEquipment, fetchEntitlement]);

  useEffect(() => { fetchClients(); fetchAgents(); }, [fetchClients, fetchAgents]);
  useEffect(() => { if (id) fetchWorkOrder(); }, [id, fetchWorkOrder]);

  // When client changes, fetch equipment and clear equipment selection
  const handleClientChange = (clientId) => {
    handleChange('client_id', clientId);
    handleChange('equipment_id', '');
    handleChange('location_id', '');
    setEntitlement(null);
    fetchEquipment(clientId);
  };

  // When equipment changes, fetch entitlement and auto-fill location
  const handleEquipmentChange = (equipmentId) => {
    handleChange('equipment_id', equipmentId);
    fetchEntitlement(equipmentId);
    const eq = equipmentList.find(e => String(e.id) === String(equipmentId));
    if (eq && eq.location_id) {
      handleChange('location_id', eq.location_id);
    }
  };

  // Checklist management
  const addChecklistItem = () => {
    if (!checklistInput.trim()) return;
    const newItem = { item: checklistInput.trim(), item_ar: checklistInputAr.trim() || '', checked: false, notes: '', checked_at: null };
    handleChange('checklist', [...formData.checklist, newItem]);
    setChecklistInput('');
    setChecklistInputAr('');
  };

  const removeChecklistItem = (index) => {
    handleChange('checklist', formData.checklist.filter((_, i) => i !== index));
  };

  // Submit
  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert(isAr ? 'العنوان مطلوب' : 'Title is required');
      return;
    }
    try {
      setSaving(true);
      const payload = { ...formData };
      // Clean empty strings to null
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (payload.checklist && payload.checklist.length === 0) payload.checklist = null;

      const url = isEdit ? `/api/v1/fsm/work-orders/${id}` : '/api/v1/fsm/work-orders';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success) {
        navigate(isEdit ? `/fsm/work-orders/${id}` : '/fsm/work-orders');
      } else {
        alert(data.message || 'Error saving work order');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Error saving work order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Layout><div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>{isAr ? 'جاري التحميل...' : 'Loading...'}</div></Layout>;
  }

  return (
    <Layout>
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto', direction: isAr ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ color: '#f9fafb', fontSize: '22px', fontWeight: '700', margin: 0 }}>
          🔧 {isEdit ? L.edit_title : L.create_title}
        </h1>
        <button style={btnOutline} onClick={() => navigate('/fsm/work-orders')}>{L.cancel}</button>
      </div>

      {/* Basic Info */}
      <div style={sectionSt}>
        <div style={sectionTitleSt}>📋 {L.basic_info}</div>
        <div style={gridSt(2)}>
          <div>
            <label style={labelSt}>{L.title} *</label>
            <input type="text" style={inputSt} value={formData.title} onChange={(e) => handleChange('title', e.target.value)} />
          </div>
          <div>
            <label style={labelSt}>{L.title_ar}</label>
            <input type="text" style={{ ...inputSt, direction: 'rtl' }} value={formData.title_ar} onChange={(e) => handleChange('title_ar', e.target.value)} />
          </div>
        </div>
        <div style={{ ...gridSt(3), marginTop: '16px' }}>
          <div>
            <label style={labelSt}>{L.type}</label>
            <select style={inputSt} value={formData.wo_type} onChange={(e) => handleChange('wo_type', e.target.value)}>
              {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelSt}>{L.priority}</label>
            <select style={inputSt} value={formData.priority} onChange={(e) => handleChange('priority', e.target.value)}>
              {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelSt}>{L.source}</label>
            <select style={inputSt} value={formData.source} onChange={(e) => handleChange('source', e.target.value)}>
              {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ ...gridSt(2), marginTop: '16px' }}>
          <div>
            <label style={labelSt}>{L.description}</label>
            <textarea style={{ ...inputSt, minHeight: '80px', resize: 'vertical' }} value={formData.description} onChange={(e) => handleChange('description', e.target.value)} />
          </div>
          <div>
            <label style={labelSt}>{L.description_ar}</label>
            <textarea style={{ ...inputSt, minHeight: '80px', resize: 'vertical', direction: 'rtl' }} value={formData.description_ar} onChange={(e) => handleChange('description_ar', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Equipment & Client */}
      <div style={sectionSt}>
        <div style={sectionTitleSt}>🏗️ {L.equipment_section}</div>
        <div style={gridSt(2)}>
          <div>
            <label style={labelSt}>{L.client}</label>
            <div style={{position:'relative'}}>
              <input type="text" style={inputSt} autoComplete="off"
                placeholder={L.select}
                value={clientSearch !== undefined && clientSearch !== '' ? clientSearch : (formData.client_id ? (clients.find(c=>String(c.id)===String(formData.client_id))?.[isAr?'name_ar':'name']||clients.find(c=>String(c.id)===String(formData.client_id))?.name||'') : '')}
                onChange={(e) => { setClientSearch(e.target.value); setShowClientDrop(true); if(!e.target.value){handleClientChange('');} }}
                onFocus={() => setShowClientDrop(true)}
                    onBlur={() => setTimeout(() => setShowClientDrop(false), 200)}
              />
              {showClientDrop && (
                <div style={{position:'absolute',top:'100%',left:0,right:0,maxHeight:'220px',overflowY:'auto',background:'#1e293b',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'0 0 8px 8px',zIndex:50}}>
                  {clients.filter(c=>{if(!clientSearch)return true;const s=clientSearch.toLowerCase();return(c.name||'').toLowerCase().includes(s)||(c.name_ar||'').toLowerCase().includes(s);}).map(c => (
                    <div key={c.id} onClick={()=>{handleClientChange(String(c.id));setClientSearch(isAr&&c.name_ar?c.name_ar:c.name);setShowClientDrop(false);}}
                      style={{padding:'9px 14px',cursor:'pointer',borderBottom:'1px solid rgba(255,255,255,0.05)',color:'#e2e8f0',fontSize:'13px'}}
                      onMouseEnter={e=>e.target.style.background='rgba(59,130,246,0.15)'}
                      onMouseLeave={e=>e.target.style.background='transparent'}>
                      {isAr && c.name_ar ? c.name_ar : c.name}
                    </div>
                  ))}
                  {clients.filter(c=>{if(!clientSearch)return true;const s=clientSearch.toLowerCase();return(c.name||'').toLowerCase().includes(s)||(c.name_ar||'').toLowerCase().includes(s);}).length===0 && (
                    <div style={{padding:'12px',color:'#64748b',textAlign:'center',fontSize:'13px'}}>No results</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div>
            <label style={labelSt}>{L.equipment}</label>
            <select style={inputSt} value={formData.equipment_id || ''} onChange={(e) => handleEquipmentChange(e.target.value)} disabled={!formData.client_id}>
              <option value="">{formData.client_id ? L.select : L.no_equipment}</option>
              {equipmentList.map(e => <option key={e.id} value={e.id}>{isAr && e.name_ar ? e.name_ar : e.name} ({e.serial_number || '-'})</option>)}
            </select>
          </div>
        </div>

        {/* Entitlement Card */}
        {entitlement && (
          <div style={{
            marginTop: '16px', padding: '16px', borderRadius: '10px',
            backgroundColor: entitlement.covered ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${entitlement.covered ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>{entitlement.covered ? '✅' : '⚠️'}</span>
              <span style={{ color: entitlement.covered ? '#10b981' : '#ef4444', fontWeight: '600', fontSize: '14px' }}>
                {entitlement.covered ? L.covered : L.not_covered}
              </span>
            </div>
            {entitlement.contract && (
              <div style={gridSt(3)}>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: '11px' }}>{L.contract}</div>
                  <div style={{ color: '#f9fafb', fontSize: '13px', fontWeight: '500' }}>{entitlement.contract.contract_number}</div>
                </div>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: '11px' }}>{L.coverage}</div>
                  <div style={{ color: '#f9fafb', fontSize: '13px', fontWeight: '500' }}>{entitlement.coverage_type}</div>
                </div>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: '11px' }}>{L.visits_left}</div>
                  <div style={{ color: '#f9fafb', fontSize: '13px', fontWeight: '500' }}>
                    {entitlement.visits_remaining !== null ? entitlement.visits_remaining : L.unlimited}
                  </div>
                </div>
              </div>
            )}
            {entitlement.sla && (entitlement.sla.response_hours || entitlement.sla.resolution_hours) && (
              <div style={{ ...gridSt(2), marginTop: '10px' }}>
                {entitlement.sla.response_hours && (
                  <div>
                    <div style={{ color: '#9ca3af', fontSize: '11px' }}>{L.sla_response}</div>
                    <div style={{ color: '#f9fafb', fontSize: '13px' }}>{entitlement.sla.response_hours} {L.hours}</div>
                  </div>
                )}
                {entitlement.sla.resolution_hours && (
                  <div>
                    <div style={{ color: '#9ca3af', fontSize: '11px' }}>{L.sla_resolution}</div>
                    <div style={{ color: '#f9fafb', fontSize: '13px' }}>{entitlement.sla.resolution_hours} {L.hours}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Schedule & Assignment */}
      <div style={sectionSt}>
        <div style={sectionTitleSt}>📅 {L.schedule_section}</div>
        <div style={gridSt(2)}>
          <div>
            <label style={labelSt}>{L.assigned_to}</label>
            <div style={{position:'relative'}}>
              <input type="text" style={inputSt} autoComplete="off"
                placeholder={L.select}
                value={agentSearch !== undefined && agentSearch !== '' ? agentSearch : (formData.assigned_to ? (agents.find(a=>String(a.id)===String(formData.assigned_to))?.full_name||'') : '')}
                onChange={(e) => { setAgentSearch(e.target.value); setShowAgentDrop(true); if(!e.target.value){handleChange('assigned_to','');} }}
                onFocus={() => setShowAgentDrop(true)}
                    onBlur={() => setTimeout(() => setShowAgentDrop(false), 200)}
              />
              {showAgentDrop && (
                <div style={{position:'absolute',top:'100%',left:0,right:0,maxHeight:'220px',overflowY:'auto',background:'#1e293b',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'0 0 8px 8px',zIndex:50}}>
                  {agents.filter(a=>{if(!agentSearch)return true;const s=agentSearch.toLowerCase();return(a.full_name||'').toLowerCase().includes(s)||(a.email||'').toLowerCase().includes(s);}).map(a => (
                    <div key={a.id} onClick={()=>{handleChange('assigned_to',String(a.id));setAgentSearch(a.full_name);setShowAgentDrop(false);}}
                      style={{padding:'9px 14px',cursor:'pointer',borderBottom:'1px solid rgba(255,255,255,0.05)',color:'#e2e8f0',fontSize:'13px'}}
                      onMouseEnter={e=>e.target.style.background='rgba(59,130,246,0.15)'}
                      onMouseLeave={e=>e.target.style.background='transparent'}>
                      {a.full_name}
                    </div>
                  ))}
                  {agents.filter(a=>{if(!agentSearch)return true;const s=agentSearch.toLowerCase();return(a.full_name||'').toLowerCase().includes(s);}).length===0 && (
                    <div style={{padding:'12px',color:'#64748b',textAlign:'center',fontSize:'13px'}}>No results</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div>
            <label style={labelSt}>{L.scheduled_date}</label>
            <input type="date" style={inputSt} value={formData.scheduled_date || ''} onChange={(e) => handleChange('scheduled_date', e.target.value)} />
          </div>
        </div>
        <div style={{ ...gridSt(2), marginTop: '16px' }}>
          <div>
            <label style={labelSt}>{L.time_start}</label>
            <input type="time" style={inputSt} value={formData.scheduled_time_start || ''} onChange={(e) => handleChange('scheduled_time_start', e.target.value)} />
          </div>
          <div>
            <label style={labelSt}>{L.time_end}</label>
            <input type="time" style={inputSt} value={formData.scheduled_time_end || ''} onChange={(e) => handleChange('scheduled_time_end', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div style={sectionSt}>
        <div style={sectionTitleSt}>☑️ {L.checklist_section}</div>

        {/* Add item row */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input type="text" placeholder={L.item_en} style={{ ...inputSt, flex: 1, minWidth: '180px' }}
            value={checklistInput} onChange={(e) => setChecklistInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addChecklistItem(); }}
          />
          <input type="text" placeholder={L.item_ar} style={{ ...inputSt, flex: 1, minWidth: '180px', direction: 'rtl' }}
            value={checklistInputAr} onChange={(e) => setChecklistInputAr(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addChecklistItem(); }}
          />
          <button onClick={addChecklistItem} style={{
            ...btnPrimary, padding: '10px 18px', whiteSpace: 'nowrap',
            opacity: checklistInput.trim() ? 1 : 0.5,
          }} disabled={!checklistInput.trim()}>
            + {L.add_item}
          </button>
        </div>

        {/* Checklist items */}
        {formData.checklist.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {formData.checklist.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
                backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <span style={{ color: '#667eea', fontSize: '14px', fontWeight: '600', minWidth: '24px' }}>
                  {idx + 1}.
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f9fafb', fontSize: '13px' }}>{item.item}</div>
                  {item.item_ar && <div style={{ color: '#9ca3af', fontSize: '12px', direction: 'rtl' }}>{item.item_ar}</div>}
                </div>
                <button onClick={() => removeChecklistItem(idx)} style={{
                  background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer',
                  fontSize: '13px', padding: '4px 8px',
                }}>
                  ✕ {L.remove}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
            {isAr ? 'لا توجد عناصر في قائمة الفحص' : 'No checklist items yet'}
          </div>
        )}
      </div>

      {/* Notes */}
      <div style={sectionSt}>
        <div style={sectionTitleSt}>📝 {L.notes_section}</div>
        <label style={labelSt}>{L.notes}</label>
        <textarea style={{ ...inputSt, minHeight: '80px', resize: 'vertical' }} value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', paddingBottom: '40px' }}>
        <button style={btnOutline} onClick={() => navigate('/fsm/work-orders')}>{L.cancel}</button>
        <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSubmit} disabled={saving}>
          {saving ? L.saving : L.save}
        </button>
      </div>
    </div>
    </Layout>
  );
};

export default WorkOrderForm;
